import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Card, Tag, Space, Input, Select, DatePicker, Button, Modal, Descriptions, message, Popconfirm, Spin } from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DollarOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { RangePicker } = DatePicker;

interface Payment {
  id: number;
  clientId: number;
  tripType: string;
  tripId: number;
  amount: number;
  bonusUsed: number;
  promoDiscount: number;
  finalAmount: number;
  paymentMethod: string;
  status: string;
  externalId: string | null;
  createdAt: string;
  completedAt: string | null;
  client: {
    id: number;
    fullName: string;
    phone: string;
  };
}

interface Transaction {
  id: number;
  transactionType: string;
  amount: number;
  description: string | null;
  timestamp: string;
  fromUser?: { fullName: string; phone: string };
  toUser?: { fullName: string; phone: string };
}

const getStatusLabels = (): Record<string, string> => ({
  PENDING: t('payments.statusPending'),
  PROCESSING: t('payments.statusProcessing'),
  COMPLETED: t('payments.statusCompleted'),
  FAILED: t('payments.statusFailed'),
  REFUNDED: t('payments.statusRefunded'),
});

const statusColors: Record<string, string> = {
  PENDING: 'orange',
  PROCESSING: 'blue',
  COMPLETED: 'green',
  FAILED: 'red',
  REFUNDED: 'purple',
};

const getPaymentMethodLabels = (): Record<string, string> => ({
  CASH: t('payment.cash'),
  UZCARD: t('payment.uzcard'),
  HUMO: t('payment.humo'),
});

const getTripTypeLabels = (): Record<string, string> => ({
  INTERCITY_TAXI: t('tripType.intercityTaxi'),
  INTERREGION_TAXI: t('tripType.interregionTaxi'),
  POSTAGE: t('tripType.postage'),
  CARGO: t('tripType.cargo'),
});

const getTransactionTypeLabels = (): Record<string, string> => ({
  TRIP_PAYMENT: t('transaction.tripPayment'),
  COMMISSION: t('transaction.commission'),
  BONUS_CREDIT: t('transaction.bonus'),
  BONUS_DEBIT: t('transaction.bonusUsed'),
  REFUND: t('transaction.refund'),
  REFERRAL_BONUS: t('transaction.referralBonus'),
  INCENTIVE_BONUS: t('transaction.incentiveBonus'),
  WALLET_TOPUP: t('transaction.walletTopup'),
  WALLET_WITHDRAWAL: t('transaction.walletWithdrawal'),
});

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');
};

export const PaymentList = () => {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Detect if we're on bus section
  const isBusSection = location.pathname.startsWith('/bus/');
  const serviceType = isBusSection ? 'bus' : 'taxi';

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/payments', {
        params: {
          search: search || undefined,
          status: status || undefined,
          paymentMethod: paymentMethod || undefined,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
          page,
          limit: pageSize,
          serviceType,
        },
      });
      setPayments(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      message.error(t('payments.loadError'));
      setPayments([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, pageSize]);

  const refetch = () => fetchPayments();

  const handleViewDetails = async (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailsModalOpen(true);
    setLoadingTransactions(true);

    try {
      const response = await apiClient.get(`/admin/payments/${payment.id}/transactions`);
      setTransactions(response.data);
    } catch {
      message.error(t('payments.transactionLoadError'));
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleRefund = async (paymentId: number) => {
    setRefunding(true);
    try {
      await apiClient.post(`/admin/payments/${paymentId}/refund`);
      message.success(t('payments.refundSuccess'));
      setDetailsModalOpen(false);
      setSelectedPayment(null);
      setTransactions([]);
      refetch();
    } catch {
      message.error(t('payments.refundError'));
    } finally {
      setRefunding(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('table.client'),
      key: 'client',
      render: (_: unknown, record: Payment) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.client?.fullName || t('common.noName')}</div>
          <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.client?.phone}</div>
        </div>
      ),
    },
    {
      title: t('table.tripType'),
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string) => (
        <Tag>{getTripTypeLabels()[type] || type}</Tag>
      ),
    },
    {
      title: t('common.amount'),
      key: 'amount',
      render: (_: unknown, record: Payment) => (
        <div>
          <div style={{ fontWeight: 500 }}>{formatCurrency(record.finalAmount)}</div>
          {(record.bonusUsed > 0 || record.promoDiscount > 0) && (
            <div style={{ fontSize: 12, color: '#7F8C8D' }}>
              {record.bonusUsed > 0 && `Bonus: -${formatCurrency(record.bonusUsed)}`}
              {record.bonusUsed > 0 && record.promoDiscount > 0 && ', '}
              {record.promoDiscount > 0 && `Promo: -${formatCurrency(record.promoDiscount)}`}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('table.paymentMethod'),
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: string) => (
        <Tag color="blue">{getPaymentMethodLabels()[method] || method}</Tag>
      ),
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {getStatusLabels()[status] || status}
        </Tag>
      ),
    },
    {
      title: t('table.date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      render: (_: unknown, record: Payment) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            title={t('common.view')}
          />
        </Space>
      ),
    },
  ];

  const transactionColumns = [
    {
      title: t('common.type'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => getTransactionTypeLabels()[type] || type,
    },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number) => formatCurrency(val),
    },
    {
      title: t('filters.from'),
      key: 'fromUser',
      render: (_: unknown, record: Transaction) => record.fromUser?.fullName || '-',
    },
    {
      title: t('filters.to'),
      key: 'toUser',
      render: (_: unknown, record: Transaction) => record.toUser?.fullName || '-',
    },
    {
      title: t('common.date'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm:ss'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <DollarOutlined style={{ marginRight: 8 }} />
        {t('payments.title')}
      </h2>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder={t('payments.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder={t('common.status')}
            value={status}
            onChange={setStatus}
            style={{ width: 150 }}
            allowClear
            options={[
              { value: 'PENDING', label: t('payments.statusPending') },
              { value: 'PROCESSING', label: t('payments.statusProcessing') },
              { value: 'COMPLETED', label: t('payments.statusCompleted') },
              { value: 'FAILED', label: t('payments.statusFailed') },
              { value: 'REFUNDED', label: t('payments.statusRefunded') },
            ]}
          />
          <Select
            placeholder={t('table.paymentMethod')}
            value={paymentMethod}
            onChange={setPaymentMethod}
            style={{ width: 150 }}
            allowClear
            options={[
              { value: 'CASH', label: t('payment.cash') },
              { value: 'UZCARD', label: t('payment.uzcard') },
              { value: 'HUMO', label: t('payment.humo') },
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
            format="YYYY-MM-DD"
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchPayments}>
            {t('common.search')}
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={payments}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total) => `${t('common.total')}: ${total}`,
          }}
        />
      </Card>

      {/* Payment Details Modal */}
      <Modal
        title={
          <Space>
            <DollarOutlined />
            <span>{t('payments.paymentDetails')} #{selectedPayment?.id}</span>
          </Space>
        }
        open={detailsModalOpen}
        onCancel={() => {
          setDetailsModalOpen(false);
          setSelectedPayment(null);
          setTransactions([]);
        }}
        footer={
          selectedPayment?.status === 'COMPLETED' ? (
            <Popconfirm
              title={t('payments.refundConfirm')}
              onConfirm={() => selectedPayment && handleRefund(selectedPayment.id)}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={refunding}
              >
                {t('payments.refundButton')}
              </Button>
            </Popconfirm>
          ) : null
        }
        width={800}
      >
        {selectedPayment && (
          <div>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label={t('table.client')}>
                {selectedPayment.client?.fullName || t('common.noName')} ({selectedPayment.client?.phone})
              </Descriptions.Item>
              <Descriptions.Item label={t('table.tripType')}>
                {getTripTypeLabels()[selectedPayment.tripType] || selectedPayment.tripType}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.id')}>
                #{selectedPayment.tripId}
              </Descriptions.Item>
              <Descriptions.Item label={t('table.paymentMethod')}>
                {getPaymentMethodLabels()[selectedPayment.paymentMethod] || selectedPayment.paymentMethod}
              </Descriptions.Item>
              <Descriptions.Item label={t('payments.originalAmount')}>
                {formatCurrency(selectedPayment.amount)}
              </Descriptions.Item>
              <Descriptions.Item label={t('payments.bonusUsed')}>
                {formatCurrency(selectedPayment.bonusUsed)}
              </Descriptions.Item>
              <Descriptions.Item label={t('payments.promoDiscount')}>
                {formatCurrency(selectedPayment.promoDiscount)}
              </Descriptions.Item>
              <Descriptions.Item label={t('payments.finalAmount')}>
                <strong>{formatCurrency(selectedPayment.finalAmount)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={t('table.status')}>
                <Tag color={statusColors[selectedPayment.status] || 'default'}>
                  {getStatusLabels()[selectedPayment.status] || selectedPayment.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('payments.externalId')}>
                {selectedPayment.externalId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.createdAt')}>
                {dayjs(selectedPayment.createdAt).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label={t('payments.completedAt')}>
                {selectedPayment.completedAt
                  ? dayjs(selectedPayment.completedAt).format('DD.MM.YYYY HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginBottom: 16 }}>{t('payments.transactionHistory')}</h4>
            <Table
              dataSource={transactions}
              columns={transactionColumns}
              rowKey="id"
              loading={loadingTransactions}
              pagination={false}
              size="small"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
